/**
 * Vendor-Shopify order sync status, as shown in the terminal.
 *
 * An order confirmed in the terminal is pushed into the vendor's own Shopify
 * store. This maps the backend's `vendor_shopify_sync.status` to a label and a
 * tone for the badge on the orders list and the order detail page.
 *
 * Tones follow the palette the terminal already uses (see shipmentProgressTone):
 *
 *   neutral  grey   — nothing has happened yet, and that's fine
 *   good     green  — the order reached the vendor's Shopify
 *   bad      red    — the push went wrong
 *
 * `pending` and `skipped` deliberately share neutral grey: neither is a problem,
 * so neither should pull the eye on a page of healthy orders. They are told
 * apart by their label, not their colour.
 *
 * Who sees any of this is decided server-side — admins always, vendors only
 * when they have a Shopify store connected, and an unconnected vendor gets
 * `vendor_shopify_sync: null` so nothing renders at all. Callers only need to
 * check for null; they must not re-implement that rule.
 */

const SYNC_STATES = {
  synced: { label: 'Synced to Shopify', shortLabel: 'Synced', tone: 'isGood' },
  failed: { label: 'Shopify sync failed', shortLabel: 'Sync failed', tone: 'isBad' },
  pending: { label: 'Shopify sync pending', shortLabel: 'Sync pending', tone: 'isNeutral' },
  skipped: { label: 'Shopify sync skipped', shortLabel: 'Skipped', tone: 'isNeutral' },
};

/**
 * Presentation for one order's sync block, or null when there is nothing to show.
 *
 * @param {{status?: string, vendor_shopify_order_id?: string|null, error?: string|null}|null} sync
 *        The order's `vendor_shopify_sync` payload.
 * @returns {{label: string, shortLabel: string, tone: string, orderId: string|null, error: string|null}|null}
 */
export function vendorSyncDisplay(sync) {
  if (!sync || !sync.status) return null;

  // An unrecognised status is still shown — never coloured as if understood.
  const state = SYNC_STATES[sync.status] || {
    label: `Shopify sync: ${sync.status}`,
    shortLabel: sync.status,
    tone: 'isNeutral',
  };

  return {
    ...state,
    orderId: sync.vendor_shopify_order_id || null,
    // Present for admins only; the server omits it otherwise.
    error: sync.error || null,
  };
}

export default vendorSyncDisplay;
